import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type DropshipLineLayoutMode = 'sales' | 'po'

const LINE_LAYOUTS = {
  sales: {
    grid: '@4xl/dropship-lines:grid-cols-[minmax(200px,1fr)_44px_88px_88px_116px_88px_96px_104px]',
    labels: ['PRODUCT', 'QTY', 'SELL', 'NET', 'DISCOUNT', 'LIST', 'SELL TOTAL', 'ACTIONS'],
  },
  po: {
    grid: '@2xl/dropship-lines:grid-cols-[minmax(200px,1fr)_44px_88px_116px_104px]',
    labels: ['PRODUCT', 'QTY', 'NET', 'DISCOUNT', 'SUPPLIER TOTAL'],
  },
} as const

/** Shared responsive column contract for editable quote rows and generated PO rows. */
export function DropshipLineColumnHeaders({ mode = 'sales' }: { mode?: DropshipLineLayoutMode }) {
  const layout = LINE_LAYOUTS[mode]
  return (
    <div
      className={cn(
        'hidden gap-2 bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground',
        mode === 'po' ? '@2xl/dropship-lines:grid' : '@4xl/dropship-lines:grid',
        layout.grid,
      )}
    >
      {layout.labels.map((label, index) => (
        <span key={label} className={index === 0 ? undefined : 'text-right'}>
          {label}
        </span>
      ))}
    </div>
  )
}

export function DropshipLineDataGrid({
  children,
  className,
  mode = 'sales',
}: {
  children: ReactNode
  className?: string
  mode?: DropshipLineLayoutMode
}) {
  return (
    <div
      data-line-data-grid
      className={cn(
        'grid gap-3 px-3 pb-3',
        mode === 'po'
          ? '@2xl/dropship-lines:items-start @2xl/dropship-lines:gap-2'
          : '@4xl/dropship-lines:items-start @4xl/dropship-lines:gap-2',
        LINE_LAYOUTS[mode].grid,
        className,
      )}
    >
      {children}
    </div>
  )
}

export function DropshipLineRowShell({
  child,
  children,
  className,
  ...props
}: { child: boolean } & ComponentPropsWithoutRef<'div'>) {
  return (
    <div {...props} className={cn('border-t', child && 'bg-muted/20', className)}>
      {children}
    </div>
  )
}

export function DropshipLineControlBand({
  child,
  children,
  className,
  mode = 'sales',
}: {
  child: boolean
  children: ReactNode
  className?: string
  mode?: DropshipLineLayoutMode
}) {
  return (
    <div
      data-line-control-band
      className={cn(
        'flex min-h-11 items-start justify-between gap-2 px-3 pt-2',
        mode === 'po'
          ? '@2xl/dropship-lines:items-center @2xl/dropship-lines:gap-3'
          : '@4xl/dropship-lines:items-center @4xl/dropship-lines:gap-3',
        child && 'pl-6',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function DropshipLineNumericGrid({
  children,
  mode = 'sales',
}: {
  children: ReactNode
  mode?: DropshipLineLayoutMode
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-x-4 gap-y-2 border-t pt-2 sm:grid-cols-3',
        mode === 'po' ? '@2xl/dropship-lines:contents' : '@4xl/dropship-lines:contents',
      )}
    >
      {children}
    </div>
  )
}

export function DropshipLineValue({
  label,
  children,
  className,
  mode = 'sales',
}: {
  label: string
  children: ReactNode
  className?: string
  mode?: DropshipLineLayoutMode
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1 text-right',
        mode === 'po' ? '@2xl/dropship-lines:gap-0' : '@4xl/dropship-lines:gap-0',
        className,
      )}
    >
      <span
        className={cn(
          'text-sm font-medium uppercase tracking-wide text-muted-foreground',
          mode === 'po' ? '@2xl/dropship-lines:sr-only' : '@4xl/dropship-lines:sr-only',
        )}
      >
        {label}
      </span>
      {children}
    </div>
  )
}

export function DropshipLineActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-end gap-1 border-t pt-2 @4xl/dropship-lines:min-h-8 @4xl/dropship-lines:border-0 @4xl/dropship-lines:pt-0">
      {children}
    </div>
  )
}
